package com.evoapps.evoshift.ui.richtext

import androidx.compose.runtime.Stable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import androidx.compose.ui.text.TextRange
import androidx.compose.ui.text.input.TextFieldValue
import com.evoapps.evoshift.domain.model.RichBlock
import com.evoapps.evoshift.domain.model.RichColor
import com.evoapps.evoshift.domain.model.RichDocument
import com.evoapps.evoshift.domain.model.RichListKind
import com.evoapps.evoshift.domain.model.RichMark
import com.evoapps.evoshift.domain.model.RichSize
import com.evoapps.evoshift.domain.model.RichSpan
import com.evoapps.evoshift.domain.model.RichStyle
import com.evoapps.evoshift.domain.model.RichText

/**
 * SH-211: the formatted lesson being edited, for one text field ([RichTextEditor]).
 *
 * The field edits plain text: the paragraphs joined by new lines ([value]). Beside it this keeps
 * each line's paragraph settings ([lines]: list kind, level, size) and the inline styles as
 * ranges of that text ([spans]). Every edit the field reports is compared with the text before
 * ([onValueChange]): the styles move with the characters around the change, typed characters
 * take the styles of the one before them (not a link's), and new lines continue the list. The
 * toolbar and the shortcuts change the styles of the selection, or (with nothing selected) of
 * what is typed next ([pending]). [document] is the [RichDocument] to store; [onChange] hears
 * every change of it.
 *
 * Pure Kotlin on Compose state, so the view-model tests drive it without a UI.
 */
@Stable
class RichEditorState(initial: RichDocument, private val onChange: (RichDocument) -> Unit = {}) {
    /** A line's paragraph settings. */
    data class Line(val list: RichListKind? = null, val level: Int = 0, val size: RichSize = RichSize.NORMAL)

    var value by mutableStateOf(TextFieldValue(initial.text))
        private set
    var lines: List<Line> by mutableStateOf(initial.blocks.map { Line(it.list, it.level, it.size) })
        private set
    /** Styles over [value]'s text (offsets of the whole text, new lines included). */
    var spans: List<RichSpan> by mutableStateOf(globalSpans(initial))
        private set
    /** With nothing selected: per style slot, what the next typed characters get (null: none of it). */
    var pending: Map<String, RichStyle?> by mutableStateOf(emptyMap())
        private set

    private fun globalSpans(doc: RichDocument): List<RichSpan> {
        var offset = 0
        val out = mutableListOf<RichSpan>()
        doc.blocks.forEach { block ->
            block.spans.forEach { out += RichSpan(it.start + offset, it.end + offset, it.style) }
            offset += block.text.length + 1
        }
        return out
    }

    private val text: String get() = value.text

    /** Where each line starts in [value]'s text. */
    fun lineStarts(of: String = text): IntArray {
        val starts = mutableListOf(0)
        of.forEachIndexed { i, c -> if (c == '\n') starts += i + 1 }
        return starts.toIntArray()
    }

    private fun lineOf(offset: Int, of: String = text): Int {
        var line = 0
        for (i in 0 until offset.coerceIn(0, of.length)) if (of[i] == '\n') line++
        return line
    }

    /** The lines the selection touches (a selection ending at a line's start leaves that line out). */
    private fun selectedLines(): IntRange {
        val sel = value.selection
        val first = lineOf(sel.min)
        val endOffset = if (sel.max > sel.min && text.getOrNull(sel.max - 1) == '\n') sel.max - 1 else sel.max
        return first..lineOf(endOffset).coerceAtLeast(first)
    }

    /** The stored form: one block per line with its spans clipped to it, sanitised. */
    fun document(): RichDocument {
        val starts = lineStarts()
        val parts = text.split('\n')
        val blocks = parts.mapIndexed { i, part ->
            val start = starts[i]
            val end = start + part.length
            val line = lines.getOrElse(i) { Line() }
            RichBlock(
                text = part,
                spans = spans.mapNotNull { span ->
                    val s = maxOf(span.start, start)
                    val e = minOf(span.end, end)
                    if (s < e) RichSpan(s - start, e - start, span.style) else null
                },
                list = line.list,
                level = line.level,
                size = line.size
            )
        }
        return RichText.sanitize(RichDocument(blocks))
    }

    private fun changed() = onChange(document())

    private fun normalize(list: List<RichSpan>) = RichText.normalizeSpans(text.length, list)

    // --- Typing -----------------------------------------------------------------------------

    /** The field's new value: a caret move, or an edit whose styles and lines follow the text. */
    fun onValueChange(next: TextFieldValue) {
        val old = text
        val new = next.text
        if (old == new) {
            if (next.selection != value.selection) pending = emptyMap()
            value = next
            return
        }
        if (new.length > RichText.MAX_TEXT && new.length > old.length) return
        // The changed range: common start and end, the start not after the new caret.
        var prefix = 0
        val shorter = minOf(old.length, new.length)
        while (prefix < shorter && old[prefix] == new[prefix]) prefix++
        if (next.selection.collapsed) prefix = minOf(prefix, next.selection.start)
        var suffix = 0
        while (suffix < shorter - prefix && old[old.length - 1 - suffix] == new[new.length - 1 - suffix]) suffix++
        val start = prefix
        val oldEnd = old.length - suffix
        val removed = old.substring(start, oldEnd)
        val inserted = new.substring(start, new.length - suffix)
        val line = lineOf(start, old)
        val starts = lineStarts(old)

        // Enter on an empty list item ends the list (or steps out one level).
        if (removed.isEmpty() && inserted == "\n") {
            val current = lines.getOrNull(line)
            val lineText = old.substring(starts[line], starts.getOrNull(line + 1)?.minus(1) ?: old.length)
            if (current?.list != null && lineText.isEmpty()) {
                lines = lines.toMutableList().also {
                    it[line] = if (current.level > 0) current.copy(level = current.level - 1) else current.copy(list = null, level = 0)
                }
                changed()
                return
            }
        }
        // Backspace at the start of a list item removes its marker first.
        if (inserted.isEmpty() && removed == "\n" && lines.getOrNull(line + 1)?.list != null &&
            value.selection.collapsed && value.selection.start == oldEnd
        ) {
            lines = lines.toMutableList().also { it[line + 1] = it[line + 1].copy(list = null, level = 0) }
            changed()
            return
        }

        spans = RichText.normalizeSpans(new.length, shiftSpans(spans, start, oldEnd, inserted.length))
        lines = shiftLines(old, line, start, starts, removed, inserted)
        value = next
        changed()
    }

    /**
     * [current] after [start]..[oldEnd] was replaced by [insertedLength] characters: spans after
     * it move, spans over it shrink, the inserted text takes the styles around it (a replaced
     * selection keeps its styles; a link only grows from inside), then [pending] applies to it.
     */
    private fun shiftSpans(current: List<RichSpan>, start: Int, oldEnd: Int, insertedLength: Int): List<RichSpan> {
        val removedLength = oldEnd - start
        fun map(offset: Int) = when {
            offset >= oldEnd -> offset - removedLength
            offset > start -> start
            else -> offset
        }
        val end = start + insertedLength
        var out = current.mapNotNull { span ->
            val s = map(span.start)
            val e = map(span.end)
            val link = span.style is RichStyle.Link
            when {
                insertedLength == 0 -> span.copy(start = s, end = e)
                // The whole span was replaced: the new text keeps its style.
                s == start && e == start && removedLength > 0 && !link -> span.copy(start = start, end = end)
                s >= start -> span.copy(start = s + insertedLength, end = e + insertedLength)
                e > start || (e == start && !link) -> span.copy(start = s, end = e + insertedLength)
                else -> span.copy(start = s, end = e)
            }.takeIf { it.start < it.end }
        }
        if (insertedLength > 0 && pending.isNotEmpty()) {
            pending.forEach { (slot, style) ->
                out = subtract(out, start, end) { it.slotName() == slot }
                if (style != null) out = out + RichSpan(start, end, style)
            }
        }
        pending = emptyMap()
        return out
    }

    private fun shiftLines(old: String, line: Int, start: Int, starts: IntArray, removed: String, inserted: String): List<Line> {
        val removedBreaks = removed.count { it == '\n' }
        val insertedBreaks = inserted.count { it == '\n' }
        val current = lines.getOrElse(line) { Line() }
        val before = lines.take(line)
        val after = lines.drop(line + 1 + removedBreaks)
        if (insertedBreaks == 0) return before + current + after
        // Typing a new line at a line's start pushes the line down unchanged.
        if (removed.isEmpty() && start == starts[line]) return before + List(insertedBreaks) { current } + current + after
        val continued = current.copy(size = if (current.size == RichSize.HEADING) RichSize.NORMAL else current.size)
        return before + current + List(insertedBreaks) { continued } + after
    }

    /** [list] without [from]..[to] of the spans [which] picks. */
    private fun subtract(list: List<RichSpan>, from: Int, to: Int, which: (RichStyle) -> Boolean): List<RichSpan> =
        list.flatMap { span ->
            if (!which(span.style) || span.end <= from || span.start >= to) listOf(span)
            else listOfNotNull(
                span.copy(end = from).takeIf { span.start < from },
                span.copy(start = to).takeIf { span.end > to }
            )
        }

    private fun RichStyle.slotName(): String = when (this) {
        is RichStyle.Mark -> mark.name
        is RichStyle.Colour -> "COLOUR"
        is RichStyle.Link -> "LINK"
    }

    // --- What the toolbar shows ------------------------------------------------------------------

    /** The style of [slot] at the caret: the pending one, else the character before it. */
    private fun styleAtCaret(slot: String): RichStyle? {
        if (slot in pending) return pending[slot]
        val caret = value.selection.start
        if (caret == 0) return null
        return spans.firstOrNull { it.style.slotName() == slot && it.start < caret && it.end >= caret && (it.style !is RichStyle.Link || it.end > caret) }?.style
    }

    /** Every character of the selection (new lines aside) has [style]. */
    private fun selectionHas(style: RichStyle): Boolean {
        val sel = value.selection
        val covered = BooleanArray(sel.length)
        spans.filter { it.style == style }.forEach { span ->
            for (i in maxOf(span.start, sel.min) until minOf(span.end, sel.max)) covered[i - sel.min] = true
        }
        return (sel.min until sel.max).all { text[it] == '\n' || covered[it - sel.min] }
    }

    fun isActive(mark: RichMark): Boolean {
        val style = RichStyle.Mark(mark)
        return if (value.selection.collapsed) styleAtCaret(mark.name) == style else selectionHas(style)
    }

    /** The colour at the caret or of the whole selection; null: none or mixed. */
    fun currentColor(): RichColor? {
        if (value.selection.collapsed) return (styleAtCaret("COLOUR") as? RichStyle.Colour)?.color
        return RichColor.entries.firstOrNull { selectionHas(RichStyle.Colour(it)) }
    }

    /** The link the caret or the selection's start is in. */
    fun currentLink(): String? {
        val at = value.selection.min
        return spans.firstOrNull { it.style is RichStyle.Link && it.start <= at && it.end > at }?.let { (it.style as RichStyle.Link).url }
    }

    private fun currentLine(): Line = lines.getOrElse(selectedLines().first) { Line() }

    fun currentList(): RichListKind? = selectedLines().map { lines.getOrElse(it) { Line() }.list }.distinct().singleOrNull()

    fun currentSize(): RichSize = currentLine().size

    val canIndent: Boolean get() = selectedLines().any { lines.getOrElse(it) { Line() }.let { l -> l.list != null && l.level < RichText.MAX_LEVEL } }

    val canOutdent: Boolean get() = selectedLines().any { lines.getOrElse(it) { Line() }.list != null }

    // --- Toolbar actions ----------------------------------------------------------------------

    private fun applyToSelection(slot: String, style: RichStyle?) {
        val sel = value.selection
        if (sel.collapsed) {
            pending = pending + (slot to style)
            return
        }
        var out = subtract(spans, sel.min, sel.max) { it.slotName() == slot }
        if (style != null) out = out + RichSpan(sel.min, sel.max, style)
        spans = normalize(out)
        changed()
    }

    /** Bold, italic, underline, strikethrough or highlight on or off for the selection (or what is typed next). */
    fun toggle(mark: RichMark) {
        val on = isActive(mark)
        applyToSelection(mark.name, if (on) null else RichStyle.Mark(mark))
    }

    /** A colour of the palette, or none ([color] null). */
    fun setColor(color: RichColor?) = applyToSelection("COLOUR", color?.let { RichStyle.Colour(it) })

    /**
     * Links the selection to [url] ([RichText.safeLink]; null removes the link). With nothing
     * selected, the link the caret is in changes. False when the address is not allowed or
     * there is nothing to link.
     */
    fun setLink(url: String?): Boolean {
        val safe = url?.let { RichText.safeLink(it) ?: return false }
        val sel = value.selection
        val range = if (!sel.collapsed) sel.min until sel.max else {
            val link = spans.firstOrNull { it.style is RichStyle.Link && it.start <= sel.start && it.end > sel.start } ?: return false
            link.start until link.end
        }
        var out = subtract(spans, range.first, range.last + 1) { it is RichStyle.Link }
        if (safe != null) out = out + RichSpan(range.first, range.last + 1, RichStyle.Link(safe))
        spans = normalize(out)
        changed()
        return true
    }

    private fun updateLines(transform: (Line) -> Line) {
        val range = selectedLines()
        lines = List(maxOf(lines.size, range.last + 1)) { i -> lines.getOrElse(i) { Line() }.let { if (i in range) transform(it) else it } }
        changed()
    }

    /** The selected lines become items of [kind], or plain paragraphs when they all are already. */
    fun toggleList(kind: RichListKind) {
        val all = selectedLines().all { lines.getOrElse(it) { Line() }.list == kind }
        updateLines { if (all) it.copy(list = null, level = 0) else it.copy(list = kind) }
    }

    fun indent() = updateLines { if (it.list != null) it.copy(level = (it.level + 1).coerceAtMost(RichText.MAX_LEVEL)) else it }

    /** One level out; from the first level out of the list. */
    fun outdent() = updateLines {
        when {
            it.list == null -> it
            it.level > 0 -> it.copy(level = it.level - 1)
            else -> it.copy(list = null)
        }
    }

    fun setSize(size: RichSize) = updateLines { it.copy(size = size) }

    /** Selects [range] of the text (tests, the link dialog). */
    fun select(range: TextRange) {
        value = value.copy(selection = TextRange(range.start.coerceIn(0, text.length), range.end.coerceIn(0, text.length)))
        pending = emptyMap()
    }

    /** What [visualTransformation] needs, as of now. */
    fun snapshot(): RichSnapshot = RichSnapshot(text, lines, normalize(spans))
}

/** The editor's text with its lines and spans at one moment (the field's visual transformation). */
data class RichSnapshot(val text: String, val lines: List<RichEditorState.Line>, val spans: List<RichSpan>) {
    /** Each line's list marker (as the readers number them). */
    val markers: List<String?>
        get() = RichText.markers(RichDocument(text.split('\n').mapIndexed { i, t ->
            val line = lines.getOrElse(i) { RichEditorState.Line() }
            RichBlock(t, list = line.list, level = line.level)
        }))
}
